import { useEffect, useCallback } from "react";

import { getHookTransactions } from "../api/api";
import { useSDKConfigStore } from "../stores/sdkConfigStore";
import { useCardStatusStore } from "../stores/cardStatusStore";
import { HookTransaction } from "../api/types";
import { useHookTransactionsStore } from "../stores/hookTransactionsStore";

export const HOOKS_PAGE_LIMIT = 20;

interface UseGetHookTransactionsReturn {
  transactions: HookTransaction[];
  isLoading: boolean;
  loadingNextPage: boolean;
  error: string | null;
  hasNext: boolean;
  loadMore: () => Promise<void>;
  refetch: () => Promise<void>;
  resetError: () => void;
}

export const useGetHookTransactions = (): UseGetHookTransactionsReturn => {
  const {
    transactions,
    isLoading,
    loadingNextPage,
    error,
    initialFetchDone,
    pagination,
  } = useHookTransactionsStore();

  const { jwt } = useSDKConfigStore();
  const { userCard } = useCardStatusStore();

  const store = useHookTransactionsStore.getState();

  const eoaAddress = jwt?.eoa || null;

  // --- API fetch executor ---
  const executeTransactionFetch = async (
    apiCall: () => Promise<HookTransaction[]>,
    onSuccess: (transactions: HookTransaction[]) => void,
    errorMessage: string,
    isNextPage = false
  ) => {
    try {
      if (isNextPage) store._setLoadingNextPage(true);
      else store._setLoading(true);

      store._setError(null);

      const transactions = await apiCall();
      onSuccess(transactions);
    } catch (error) {
      console.error(`[useGetHookTransactions] ${errorMessage}:`, error);
      const message = error instanceof Error ? error.message : errorMessage;
      store._setError(message);
    } finally {
      if (isNextPage) store._setLoadingNextPage(false);
      else store._setLoading(false);
    }
  };

  // --- Success Handlers ---
  const handleInitialFetchSuccess = (transactions: HookTransaction[]) => {
    store._setTransactions(transactions, {
      limit: HOOKS_PAGE_LIMIT,
      offset: transactions.length, // next page should start after these
      hasNext: transactions.length === HOOKS_PAGE_LIMIT,
    });
    store._setInitialFetchDone(true);
  };

  const handleLoadMoreSuccess = (transactions: HookTransaction[]) => {
    store._appendTransactions(transactions, {
      limit: HOOKS_PAGE_LIMIT,
      offset: (pagination?.offset ?? 0) + HOOKS_PAGE_LIMIT, // always + HOOKS_PAGE_LIMIT
      hasNext: transactions.length === HOOKS_PAGE_LIMIT,
    });
  };

  // --- Initial fetch ---
  useEffect(() => {
    const fetchInitialHookTransactions = async () => {
      if (eoaAddress && userCard?.cardID && !initialFetchDone && !isLoading) {
        await executeTransactionFetch(
          () => getHookTransactions(HOOKS_PAGE_LIMIT, 0),
          handleInitialFetchSuccess,
          "Failed to fetch initial transactions"
        );
      }
    };
    fetchInitialHookTransactions();
  }, [eoaAddress, userCard?.cardID]);

  // --- Load More ---
  const loadMore = useCallback(async () => {
    if (isLoading || loadingNextPage || !eoaAddress || !userCard?.cardID) {
      return;
    }

    await executeTransactionFetch(
      () =>
        getHookTransactions(
          HOOKS_PAGE_LIMIT,
          pagination.offset + pagination.limit
        ),
      handleLoadMoreSuccess,
      "Failed to load more transactions",
      true
    );
  }, [isLoading, loadingNextPage, eoaAddress, userCard?.cardID, pagination]);

  // --- Refetch ---
  const refetch = useCallback(async () => {
    if (!jwt || !userCard?.cardID || isLoading) return;

    await executeTransactionFetch(
      () => getHookTransactions(HOOKS_PAGE_LIMIT, 0),
      handleInitialFetchSuccess,
      "Failed to refetch transactions"
    );
  }, [eoaAddress, userCard?.cardID, isLoading]);

  // --- Reset Error ---
  const resetError = useCallback(() => {
    store._setError(null);
  }, []);

  // --- hasNext Computation ---
  const hasNext = pagination?.hasNext ?? false;

  return {
    transactions,
    isLoading,
    loadingNextPage,
    error,
    hasNext,
    loadMore,
    refetch,
    resetError,
  };
};
